import React, { useMemo, useCallback } from "react";

import "./VotingCard.css";
import HeaderElement from "../../HeaderElement/HeaderElement";
import VoteType from "./VoteType/VoteType";
import VoteButton from "../../Buttons/VoteButton/VoteButton";
import SendVote from "../../Buttons/SendVote/SendVote";
import popupicon from "../../../assets/icons/popup.svg";
import StandardPopup from "../../Global/StandardPopup/StandardPopup";
import { UserAuth } from "../../../context/AuthContext";
import { useUI } from "../../../hooks/usePermission";
import { useApi } from "../../../hooks/useApi";
import { useNavigate } from "react-router-dom";
import PopUpResponseButton from "../../Buttons/PopUpResponseButton/PopUpResponseButton";
import strings from "../../../locales/ro.json";
import Modal from "@mui/material/Modal";
import LoadingCircle from "../../Global/LoadingCircle";
import VoteStatusButton from "../../Buttons/VoteStatusButton/VoteStatusButton";
import statuteFromGroups from "../../../utils/statuteFromGroups";
import config from "../../../config";

/*
 This is the voting card component, that contains the title, question, options of a ballot and a button for the user to vote

 Props:

 ballot = a ballot object that has the title,question, type and options fields populated according to the costelDB documentation
*/

const VotingCard = React.memo(({ ballot = { options: ["test1", "test2"] }, canVote=true , ballotId}) => {
  const navigate = useNavigate();
  const { user } = UserAuth();
  const { api } = useApi();
  const [selectedVotes, setSelectedVotes] = React.useState([]);
  const [openClosingPopup, setOpenClosingPopup] = React.useState(false);
  const [openInvalidationPopup, setOpenInvalidationPopup] = React.useState(false);

  const [popupOpen, setPopupOpen] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false); 

  const [ballotData, setBallotData] = React.useState(ballot);
  const [votersData, setVotersData] = React.useState({
    seniors: [],
    juniors: [],
    alumni: [],
    voters: null,
    users: null,
    requestsFinished: false,
    isLoading: true
  });
  const [quorum, setQuorum] = React.useState(null);

  // Memoize expensive calculations
  const hasVoted = useMemo(() => 
    votersData.voters?.voters?.includes(user?.profile?.name),
    [votersData.voters?.voters, user?.profile?.name]
  );

  const selectedVotesText = useMemo(() => 
    selectedVotes.map(voteIndex => `"${ballotData?.question?.options[voteIndex]?.name}"`).join(", "),
    [selectedVotes, ballotData?.question?.options]
  );

  const canManageBallots = useUI('action.ballots.manage');

  const canShowAdminButtons = useMemo(() => 
    selectedVotes.length === 0 && canManageBallots,
    [selectedVotes.length, canManageBallots]
  );

  // Memoize callbacks
  const handleVoteClick = useCallback((optionNumber) => {
    if (!canVote || hasVoted) return;

    setSelectedVotes(prevVotes => {
      if (prevVotes.includes(optionNumber)) {
        return prevVotes.filter(vote => vote !== optionNumber);
      }
      
      if (!ballotData.question.is_multiple_choice) {
        return [optionNumber];
      }
      
      return [...prevVotes, optionNumber];
    });
  }, [canVote, hasVoted, ballotData.question.is_multiple_choice]);

  const closeBallot = useCallback(async () => {
    try {
      const ballotIdParent = ballotData?.parentBallotId || ballotId;
      await api(`ballot/${ballotIdParent}`, "PATCH");

      if (ballotData?.parentBallotId) {
        navigate(`/ballot/${ballotData.parentBallotId}`);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error closing ballot:", error);
    }
  }, [ballotId, ballotData?.parentBallotId, api, navigate]);

  const deleteBallot = useCallback(async () => {
    try {
      const requests = [api(`ballot/${ballotId}`, "DELETE")];

      if (ballotData?.juniorBallotId) {
        requests.push(api(`ballot/${ballotData.juniorBallotId}`, "DELETE"));
      }

      if (ballotData?.alumniBallotId) {
        requests.push(api(`ballot/${ballotData.alumniBallotId}`, "DELETE"));
      }

      await Promise.all(requests);
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error deleting ballot:", error);
    }
  }, [ballotId, ballotData?.juniorBallotId, ballotData?.alumniBallotId, api, navigate]);

  const sendVote = useCallback(async () => {
    try {
      const optionIDs = selectedVotes.map(voteIndex => ballotData.question.options[voteIndex].id);
      await api(`ballot/${ballotId}/vote`, "POST", { optionIDs });
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error sending vote:", error);
    }
  }, [ballotId, ballotData.question.options, api, navigate, selectedVotes]);

  React.useEffect(() => {
    const fetchUsersData = async () => {
      if (!user?.accessToken) return;

      setVotersData(prev => ({ ...prev, isLoading: true }));
      try {
        const votersResponse = await api(`ballot/${ballotId}/voters`, "GET");

        if (votersResponse) {
          const isGASplitVote = votersResponse.isGASplitVote || false;

          let seniors, juniors, alumni, totalVoters;
          
          if (isGASplitVote) {
            // Use the three separate arrays for split votes
            const parentBallotVoters = votersResponse.seniorBallotVoters || [];
            const juniorBallotVoters = votersResponse.juniorBallotVoters || [];
            const alumniBallotVoters = votersResponse.alumniBallotVoters || [];

            
            seniors = parentBallotVoters;
            juniors = juniorBallotVoters;
            alumni = alumniBallotVoters;
            totalVoters = parentBallotVoters.length + juniorBallotVoters.length + alumniBallotVoters.length;
            
          } else {
            // Use the single voters array for non-split votes
            const voters = votersResponse.voters || [];
            seniors = voters;
            juniors = [];
            alumni = [];
            totalVoters = voters.length;
          }

          setVotersData({
            seniors,
            juniors,
            alumni,
            voters: { voters: isGASplitVote ? 
              Array.from(new Set([...seniors, ...juniors, ...alumni])) : 
              seniors 
            },
            totalVoters,
            isGASplitVote,
            requestsFinished: true,
            isLoading: false
          });
        }
      } catch (error) {
        console.error("Error fetching voters data:", error);
        setVotersData(prev => ({ ...prev, isLoading: false }));
      }
    };

    const fetchQuorum = async () => {
      try {
        const users = await api("users", "GET");
        let juniors = 0;
        let seniors = 0;
        let alumni = 0;
        
        users.forEach((u) => {
          const statute = statuteFromGroups(u);
          if (statute === "junior") juniors++;
          else if (statute === "senior") seniors++;
          else if (statute === "alumni") alumni++;
        });

        const calculatedQuorum = Math.ceil((seniors + (juniors > 0 ? 1 : 0) + (alumni > 0 ? 1 : 0)) / 2);
        setQuorum(calculatedQuorum);
      } catch (error) {
        console.error("Error fetching users for quorum:", error);
      }
    };

    fetchUsersData();
    fetchQuorum();
  }, [ballotId, api, user?.accessToken]);

  React.useEffect(() => {
    if (!user?.accessToken) {
      return;
    }

    let eventSource = null;
    const protocol = config.isDev === true ? "http://" : "https://";
    const port = config.isDev === true ? `:${config.port}` : "";
    const sseUrl = `${protocol}${config.backend}${port}/ballot/${ballotId}/sse/?token=${user.accessToken}`;

    try {
      eventSource = new EventSource(sseUrl);
      
      eventSource.onopen = () => {
      };
      
      eventSource.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'vote') {
            
            // Update voters data with the new voter
            setVotersData(prev => {
              const newVoter = message.data.user.name;
              const userGroups = message.data.user.groups || [];
              
              let updatedSeniors = [...prev.seniors];
              let updatedJuniors = [...prev.juniors];
              let updatedAlumni = [...prev.alumni];

              if (prev.isGASplitVote) {
                // Maintain group division based on user groups
                if (userGroups.includes("Seniors")) {
                  updatedSeniors = [...new Set([...updatedSeniors, newVoter])];
                } else if (userGroups.includes("Juniors")) {
                  updatedJuniors = [...new Set([...updatedJuniors, newVoter])];
                } else if (userGroups.includes("Alumni")) {
                  updatedAlumni = [...new Set([...updatedAlumni, newVoter])];
                }
              } else {
                // Add to seniors (which represents all members when not split)
                updatedSeniors = [...new Set([...updatedSeniors, newVoter])];
              }
              
              const allVoters = [...new Set([...updatedSeniors, ...updatedJuniors, ...updatedAlumni])];
              const totalVoters = updatedSeniors.length + updatedJuniors.length + updatedAlumni.length;
              
              return {
                ...prev,
                seniors: updatedSeniors,
                juniors: updatedJuniors,
                alumni: updatedAlumni,
                voters: { voters: allVoters },
                totalVoters
              };
            });
          } else if (message.type === 'close') {
            navigate(`/ballot/${message.data.redirectBallotID}`);
          } else if (message.type === 'invalidate') {
            navigate('/');
          }
        } catch (error) {
          console.error("Error processing SSE data in VotingCard:", error);
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE Error in VotingCard:", error);
        if (eventSource) {
          eventSource.close();
        }
      };
    } catch (error) {
      console.error("Error creating SSE connection in VotingCard:", error);
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [user, ballotId, navigate]);

  const computedVotesCount = votersData.seniors.length + (votersData.juniors.length > 0 ? 1 : 0) + (votersData.alumni.length > 0 ? 1 : 0);
  const isQuorumReached = quorum !== null && computedVotesCount >= quorum;

  return (
    <div className="votingCardWrapper">
      <HeaderElement headerText={ballotData.title} />
      <div className="metadataWrapper">
        <div className="ballotQuestion">{ballotData.question.question_text}</div>
        <div className="onlyOneOption">{ballotData.question.is_multiple_choice ? strings.votingCard.multipleChoice : strings.votingCard.singleChoice}</div>
        <VoteType voteType={ballotData.showVotesPublicly == 0 ? 0 : 1} />
      </div>
      <div className="votingCardContainer">
        <Modal 
          open={openModal} 
          onClose={() => setOpenModal(false)}
        >
          <div className="voteStatusModalContainer">
            <div className="voteStatusModalVotersContainer">
              {votersData.isLoading ? (
                <LoadingCircle />
              ) : ballotData && votersData.voters?.voters ? (
                <div className="voteStatusModalVoters">
                  <div className="voteStatusModalHeader">{strings.votingCard.alreadyVoted}</div>
                  <div className="voteStatusModalCount" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <span>{votersData.voters.voters.length} {strings.votingCard.voters}</span>
                    {ballotData.isGeneralAssemblySplitVote && (
                      <span>
                        {computedVotesCount} {strings.votingCard.votes}
                      </span>
                    )}
                    {ballotData.isGeneralAssemblySplitVote && quorum !== null && (
                      <span style={{ fontWeight: 600, color: isQuorumReached ? '#166534' : 'var(--eestec-red)' }}>
                        {strings.usermanagement.statsModal.quorum}: {quorum} {isQuorumReached ? "(atins)" : ""}
                      </span>
                    )}
                  </div>
                  
                  {votersData.seniors.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        {votersData.isGASplitVote ? strings.votingCard.seniors : strings.votingCard.members} ({votersData.seniors.length}):
                      </div>
                      {votersData.seniors.map((senior, index) => (
                        <div key={`senior-${index}`} style={{textAlign: "center"}}>{senior}</div>
                      ))}
                    </>
                  )}

                  {votersData.juniors.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        {strings.votingCard.juniors} ({votersData.juniors.length}):
                      </div>
                      {votersData.juniors.map((junior, index) => (
                        <div key={`junior-${index}`} style={{textAlign: "center"}}>{junior}</div>
                      ))}
                    </>
                  )}

                  {votersData.alumni.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        {strings.votingCard.alumni} ({votersData.alumni.length}):
                      </div>
                      {votersData.alumni.map((alumni, index) => (
                        <div key={`alumni-${index}`} style={{textAlign: "center"}}>{alumni}</div>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="voteStatusModalVoters">
                  <div className="voteStatusModalHeader">{strings.votingCard.noVotesYet}</div>
                </div>
              )}
            </div>

            <div className="voteStatusModalButton">
              <PopUpResponseButton
                type="cancel"
                text={strings.votingCard.close}
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </Modal>
        <StandardPopup message={strings.editvote.sendVotePopup} message2={selectedVotesText} icon={popupicon} yesFcn={async () => {setPopupOpen(false); await sendVote();}} noFcn={() => {setPopupOpen(false)}} isOpen={popupOpen}/>
        {ballotData.question.options && <div className="voteButtonsContainer">
          {ballotData.question.options.map((option, optionNumber) => {
            return (
              <VoteButton
                key={option.id || optionNumber}
                text={option.name}
                onClick={() => handleVoteClick(optionNumber)}
                isPressed={selectedVotes.includes(optionNumber)}
              />
            );
          })}
        </div>}
        <div className="sendVoteButtonContainer">
          {!hasVoted ? (
            selectedVotes.length > 0 && (
              <SendVote
                onClickFunction={() => {
                  setPopupOpen(true);
                }}
              />
            )
          ) : (!canManageBallots && <div className="alreadyVoted">{strings.votingCard.youAlreadyVoted}</div>)}
          {
            canShowAdminButtons && (
              <div className="editBallotButtonsContainer">
                <PopUpResponseButton
                  type={"yes"}
                  text={strings.editvote.closeAndShow}
                  onClick={() => {
                    setOpenClosingPopup(true);
                  }}
                />
                <PopUpResponseButton
                  type={"cancel"}
                  text={strings.editvote.invalidate}
                  onClick={() => {
                    setOpenInvalidationPopup(true);
                  }}
                />
              </div>
            )
          }
          <StandardPopup message={strings.editvote.invalidatePopup} isOpen={openInvalidationPopup} yesFcn={deleteBallot} noFcn={() => {setOpenInvalidationPopup(false)}} icon={popupicon}/>
          <StandardPopup message={strings.editvote.showResultsPopup} isOpen={openClosingPopup} yesFcn={closeBallot} noFcn={() => {setOpenClosingPopup(false)}} icon={popupicon}/>
        </div>
        <div className="buttonsWrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
          <VoteStatusButton onClick={() => setOpenModal(true)} />
          <div style={{ display: 'flex', gap: '30px', fontWeight: '600', color: 'var(--dark-grey)', flexWrap: 'wrap', justifyContent: 'center' }}>
            {votersData.voters?.voters && !votersData.isLoading && (
              <div className="editBallotVotesCount">
                Total Voturi: {votersData.totalVoters}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

VotingCard.displayName = 'VotingCard';

export default VotingCard;